/**
 * Central configuration. All hardcoded URLs, API keys, region names, and
 * data.gov.il resource IDs live here so callers never embed them.
 *
 * Anything sensitive (Twilio, SMS gateway keys) MUST stay in Firebase Functions
 * — this file is shipped to the client and is therefore public-readable.
 */

export const Firebase = /** @type {const} */ ({
  config: {
    apiKey: 'AIzaSyDLUdJyF0Kv46xb2gJmzh9nykJt-jZ2U34',
    authDomain: 'call-to-car-6542b.firebaseapp.com',
    databaseURL: 'https://call-to-car-6542b-default-rtdb.europe-west1.firebasedatabase.app',
    projectId: 'call-to-car-6542b',
    storageBucket: 'call-to-car-6542b.firebasestorage.app',
    messagingSenderId: '740425848873',
    appId: '1:740425848873:web:16101ec70c39cc6503e438',
    measurementId: 'G-XMPENWLC1F',
  },
  functionsRegion: 'europe-west1',
});

export const Ckan = /** @type {const} */ ({
  baseUrl: 'https://data.gov.il/api/3/action/datastore_search',
  // We used to race our function against `codetabs` and `corsproxy.io`
  // public proxies via `Promise.any`. Even when our function wins, the
  // browser still logs the rejected racers' network errors (403s) to the
  // console — visually noisy and confusing during debugging. Our function
  // is reliable enough that the fallbacks add nothing but noise, so the
  // list now contains just our endpoint. The direct (no-proxy) transport
  // in ckan.js still runs in parallel and wins on native (Capacitor)
  // where CORS doesn't apply.
  proxies: [
    'https://europe-west1-call-to-car-6542b.cloudfunctions.net/ckanProxy?u=',
  ],
  resources: {
    /** Main vehicle registry (shnat_yitzur, tozeret, tokef_dt, mivchan_acharon_dt). */
    vehicles: '053cea08-09bc-40ec-8f7a-156f0677aff3',
    /** Ownership history (baalut_dt). */
    ownership: 'bb2355dc-9ec7-4f06-9c3f-3344672171da',
    /** MOT inspections / test dates. */
    inspections: '56063a99-8a3e-4ff4-912e-5966c0279bad',
    /** Additional vehicle datasets used as fallback when primary returns no record. */
    vehicleFallbacks: [
      'cd3acc5c-03c3-4c89-9c54-d40f93c0d790',
      'bf9df4e2-d90d-4c0a-a400-19e15af8e95f',
    ],
    /** Public gas-station registry with geographic coordinates (חברה / שם_תחנה /
     *  כתובת / רשות_מקומית / נ.צ. אורך / נ.צ. רוחב). Used by the in-app
     *  auto-services screen. */
    gasStations: '5537a0ef-3eeb-449c-90c8-51e27564f0cb',
    /** Authorised garages + vehicle-inspection centres registry (Ministry of
     *  Transport). Single dataset; we split by `sug_mosah`. No lat/lon — we
     *  filter by `yishuv` (city) instead. Fields: shem_mosah, ktovet, yishuv,
     *  telephone, sug_mosah, miktzoa. */
    garagesAndInspections: 'bb68386a-a331-4bbc-b668-bba2766d517d',
    /** "כלי רכב עם תג חניה לנכה" — Ministry of Transport inclusion-only list.
     *  Fields (case + space MATTER — they're capitalised with a literal
     *  space): `MISPAR RECHEV` (numeric), `TAARICH HAFAKAT TAG`, `SUG TAV`.
     *  Presence of a record for the plate = the vehicle has a disability
     *  parking tag. Updated daily. */
    disabledTag: 'c8b9f9c8-4612-4068-934f-d4acd2e3c06e',
    /** "הודעות יצרני הרכב RECALL" — manufacturer recall notices, indexed by
     *  TOZAR_CD (manufacturer code) + DEGEM (model). Same dataset the
     *  server-side `dailyVehicleAnomalyCheck` Cloud Function uses. Fields:
     *  RECALL_ID, TOZAR_CD, DEGEM, YEAR_FROM, YEAR_TO, RECALL_DESC, WEBSITE. */
    recalls: '2c33523f-87aa-44ec-a736-edbb0a82975e',
  },
  timeoutMs: 8000,
});

/**
 * OpenStreetMap Overpass — used for categories that aren't in data.gov.il
 * (parking lots, car washes, car-parts shops). The Overpass instance is a
 * volunteer-run service: pick a mirror with reasonable uptime and don't
 * hammer it.
 */
export const Overpass = /** @type {const} */ ({
  endpoints: [
    // Public volunteer-run Overpass mirrors. We race them in parallel so the
    // first non-empty response wins — useful because individual mirrors are
    // saturated or rate-limit heavy queries (especially `shop=*` filters).
    //
    // NOTE: `overpass.osm.ch` was DROPPED — its region coverage doesn't seem
    // to include Israel (returns 0 elements very quickly for every query we
    // tested), which when combined with `Promise.any` used to make it
    // "win" the race with no data. Live mirrors only from here on.
    'https://overpass.kumi.systems/api/interpreter',
    'https://overpass.private.coffee/api/interpreter',
    'https://overpass-api.de/api/interpreter',
  ],
  /** Per-endpoint timeout. The client races every endpoint in parallel and
   *  uses the first NON-EMPTY response (empty `{ elements: [] }` is treated
   *  as rejection inside the race), so this is also the hard ceiling the
   *  user waits before falling back to "no results". 12s is generous but
   *  Overpass mirrors have been saturated lately. */
  timeoutMs: 12000,
  /** Default search radius for "around me" queries. */
  defaultRadiusM: 5000,
});

export const LocationIq = /** @type {const} */ ({
  baseUrl: 'https://us1.locationiq.com/v1/reverse',
  apiKey: 'pk.2712cb5b1713dff48e74033c8e1b3c2c',
  timeoutMs: 6000,
});

/** Default location when GPS is unavailable. Eilat — matches legacy fallback. */
export const DefaultLocation = /** @type {const} */ ({ lat: 29.5577, lon: 34.9519 });

/**
 * Public Google Apps Script SMS endpoint. Used for non-OTP chat SMS.
 * Updated 2026-05-16 to the currently-deployed GAS URL. The deployment
 * expects `{ phone, message }` payload with `text/plain` Content-Type (no-cors
 * fetch from browser — GAS doPost reads it from `e.postData.contents`).
 */
export const SmsGateway = /** @type {const} */ ({
  publicUrl:
    'https://script.google.com/macros/s/AKfycbzb_OuDpixnWJ5hJ2wI2v2ZDxD7Cl-j5cInqnjL6R1So77iFI7R1WoS7sStCXqrHacO6g/exec',
});

/**
 * OTP delivery now goes through the `sendOtp` Cloud Function — SMS4Free
 * credentials live in Firebase Secret Manager (server-only). This export is
 * kept (empty) only for legacy callers that imported the name; remove once
 * they're all migrated.
 *
 * @deprecated server-side OTP — do not use from the client.
 */
export const Sms4Free = /** @type {const} */ ({
  appName: 'AutoMe',
});

/** Rate limits enforced client-side (server-side limits are stricter). */
export const Limits = /** @type {const} */ ({
  smsPerHour: 1,
  searchRetries: 2,
  callMinDurationSeconds: 60,
  callCooldownMs: 60 * 60 * 1000,
});

/**
 * AdMob — production unit IDs plus Google test IDs for debugging.
 * Toggle `production: false` to force test IDs without recompiling new ones.
 */
export const AdMob = /** @type {const} */ ({
  production: true,
  appId: 'ca-app-pub-1951495342839173~7921860264',
  units: {
    banner:       'ca-app-pub-1951495342839173/2920815022',
    interstitial: 'ca-app-pub-1951495342839173/2266494315',
    rewarded:     'ca-app-pub-1951495342839173/4059594732',
  },
  testUnits: {
    banner:       'ca-app-pub-3940256099942544/6300978111',
    interstitial: 'ca-app-pub-3940256099942544/1033173712',
    rewarded:     'ca-app-pub-3940256099942544/5224354917',
  },
  rateLimits: {
    interstitialMs: 3 * 60 * 1000,
    rewardedMs:     2 * 60 * 1000,
  },
  retry: {
    maxAttempts: 3,
    baseDelayMs: 5_000,
    maxDelayMs:  60_000,
  },
});

/**
 * Web ads (Google AdSense — browser only, not used on native Capacitor
 * builds where AdMob handles ads).
 *
 * Two-step setup:
 *   1. Sign up at adsense.google.com and submit autome.co.il for review
 *      (takes ~1-14 days). Once approved, fill in `publisherId` below
 *      (format `ca-pub-XXXXXXXXXXXXXXXX`).
 *   2. In the AdSense console create three "Display Ad" units (Responsive
 *      is fine) and paste each slot ID into `slots.{top,mid,sticky}`.
 *
 * While `publisherId` is empty this module is a no-op — no script is
 * loaded, no requests are made, and the ad-slot DIVs in `www/index.html`
 * stay hidden (see `.ad-slot:empty` rule in `home.css`).
 */
export const WebAds = /** @type {const} */ ({
  publisherId: 'ca-pub-1951495342839173',
  slots: {
    top:    '',
    mid:    '',
    sticky: '',
  },
});

/**
 * Web Push (FCM browser) configuration. The VAPID key is the Web Push
 * "public key" generated in Firebase Console → Project Settings →
 * Cloud Messaging → Web configuration. Without it, web tokens can't be
 * minted and browser push silently degrades.
 *
 * Native (Android/iOS) push doesn't need this — it uses google-services.json.
 */
export const WebPush = /** @type {const} */ ({
  /**
   * Public VAPID key — copied from Firebase Console → Project Settings →
   * Cloud Messaging → "Web Push certificates" → the current pair.
   * Public key only (87 chars, starts with `B`). The matching private key
   * stays in the console and is referenced server-side only.
   */
  vapidKey:
    'BDaRbrT9H9xO-SGV81De4Hzwi6TLduqEnc6lOKc3f28VibSIzHmxoUHeBafJWU-LQbRoNo_H2rET7FXLcYgBnLg',
  serviceWorkerPath: '/firebase-messaging-sw.js',
});

/**
 * Authentication knobs. Values mirror the legacy hardcoded constants but
 * are centralised so future phases can move them to Remote Config / server.
 */
export const Auth = /** @type {const} */ ({
  /** Stamped on user records when they accept terms. Bump when terms change. */
  termsVersion: 'v1',
  /** Max failed login attempts per phone per day before permanent block. */
  maxAttemptsPerDay: 3,
  /** Support contact shown in lockout / block messages. */
  supportEmail: 'itsikapps@gmail.com',
  /**
   * Realtime DB path holding the admin configuration. Reads expose admin UIDs
   * and admin phone numbers so the client can stop hardcoding them.
   *   /config/admins/uids/{uid}      = true
   *   /config/admins/phones/{phone}  = true
   */
  adminConfigPath: 'config/admins',
  /**
   * Test-bypass triple that skips OTP + index claims + device tracking.
   * Used by the Google Play review team so their automated check can sign
   * into the app without receiving an SMS (the reviewers don't have access
   * to the test phone).
   *
   * Values must match the credentials registered in the Play Console under
   * "App access → Login credentials":
   *   - Phone:    +972 59-555-8022  →  normalized: 0595558022
   *   - Plate:    62194702           (digits-only)
   *   - Year:     2021
   *
   * All three fields are required to match — relying on just phone+plate
   * would let anyone who guessed the pair log in regardless of year, which
   * the user explicitly called out as a security regression.
   *
   * @type {{ plate: string; phone: string; year: string } | null}
   */
  testBypass: {
    plate: '62194702',
    phone: '0595558022',
    year: '2021',
  },
  /**
   * No longer used — security rules are now claim-based and verify
   * `auth.uid == ownerId` directly. Kept (empty) only because some legacy
   * modules still reference the name; remove fully when those are migrated.
   *
   * @deprecated
   */
  rtdbWriteBypass: '',
  /** OTP length and TTL — server will eventually enforce these. */
  otpDigits: 6,
  otpTtlMs: 5 * 60 * 1000,
});
